import { useState } from 'react';
import { Badge, Button, HoverCard } from '@round/creatorbase-ui';
import { ConfigurationItem } from '../../../../../helpers';
import { ConfigurationItemBadge } from '../../../../../components/ConfigurationItemBadge/ConfigurationItemBadge';
import { useFittedItemCount } from './useFittedItemCount';
import styles from './ConfigurationGroup.module.css';

/**
 * Past this a group gets a fold. It opens expanded all the same — the page exists to show the setup
 * in full, and the fold is there to put a long list away again.
 */
const MAX_UNFOLDED_ITEM_COUNT = 8;

const SHOW_FEWER_LABEL = 'Show fewer';

/** A group with nothing in it says so, rather than leaving a title over blank space. */
const EMPTY_LABEL = 'None';

type Props = {
    title: string;
    description: string;
    items: ConfigurationItem[];
    /** Folds the group into a single row of badges once the setup has scrolled behind the headline. */
    isCompact?: boolean;
};

export const ConfigurationGroup = ({ title, description, items, isCompact }: Props) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const { containerRef, measureRef, visibleItemCount } = useFittedItemCount(items.length);

    const handleToggleFold = () => setIsExpanded((wasExpanded) => !wasExpanded);

    if (isCompact) {
        const hiddenItemCount = items.length - visibleItemCount;

        return (
            <div className={styles.compactGroup}>
                <span className={styles.compactGroupTitle}>{title}</span>

                <div ref={containerRef} className={styles.compactItems}>
                    {items.slice(0, visibleItemCount).map((item) => (
                        <ConfigurationItemBadge key={item.key} item={item} />
                    ))}

                    {hiddenItemCount > 0 && (
                        <HoverCard>
                            <HoverCard.Trigger
                                className={styles.overflowTrigger}
                                render={<button type="button" aria-label={`Show all ${title.toLowerCase()}`} />}
                            >
                                <Badge variant="outline" className={styles.overflowCount}>
                                    +{hiddenItemCount}
                                </Badge>
                            </HoverCard.Trigger>

                            <HoverCard.Content className={styles.overflowCard}>
                                <p className={styles.overflowCardTitle}>{title}</p>
                                <div className={styles.overflowCardItems}>
                                    {items.map((item) => (
                                        <ConfigurationItemBadge key={item.key} item={item} />
                                    ))}
                                </div>
                            </HoverCard.Content>
                        </HoverCard>
                    )}
                </div>

                {/* The full list, laid out on one unwrapped line and taken out of the flow: what the
                    row above is measured against. */}
                <div ref={measureRef} aria-hidden="true" className={styles.measureLayer}>
                    {items.map((item) => (
                        <ConfigurationItemBadge key={item.key} item={item} />
                    ))}
                    {/* The widest count the group could fall back to, so the room held for it is
                        never short — a row too narrow for even one badge alongside it holds none. */}
                    <Badge variant="outline" className={styles.overflowCount}>
                        +{items.length}
                    </Badge>
                </div>
            </div>
        );
    }

    const isFoldable = items.length > MAX_UNFOLDED_ITEM_COUNT;
    const visibleItems = isFoldable && !isExpanded ? items.slice(0, MAX_UNFOLDED_ITEM_COUNT) : items;

    return (
        <div className={styles.group}>
            <div className={styles.groupHeader}>
                <h3 className={styles.groupTitle}>{title}</h3>
                <p className={styles.groupDescription}>{description}</p>
            </div>

            {items.length ? (
                <div className={styles.groupItems}>
                    {visibleItems.map((item) => (
                        <ConfigurationItemBadge key={item.key} item={item} />
                    ))}
                </div>
            ) : (
                <span className={styles.groupEmpty}>{EMPTY_LABEL}</span>
            )}

            {isFoldable && (
                <Button variant="bare" size="xs" className={styles.foldToggle} onClick={handleToggleFold}>
                    {isExpanded ? SHOW_FEWER_LABEL : `Show all ${items.length}`}
                </Button>
            )}
        </div>
    );
};
